import { ICampaignLead, ISequenceStep } from '../interfaces/models/campaign';
import { cleanPersonName, resolveLeadNameParts, looksLikeEmail } from './personName';

export type CampaignVariableKey = 'firstName' | 'lastName' | 'company' | 'title' | 'senderName';

export interface ICampaignVariableFallbacks {
  firstName?: string;
  lastName?: string;
  company?: string;
  title?: string;
  senderName?: string;
}

export const CAMPAIGN_TEMPLATE_VARIABLES: {
  key: CampaignVariableKey;
  token: string;
  label: string;
}[] = [
  { key: 'firstName', token: '{{firstName}}', label: 'First name' },
  { key: 'lastName', token: '{{lastName}}', label: 'Last name' },
  { key: 'company', token: '{{company}}', label: 'Company' },
  { key: 'title', token: '{{title}}', label: 'Title' },
  { key: 'senderName', token: '{{senderName}}', label: 'Sender name' },
];

export const DEFAULT_CAMPAIGN_VARIABLE_FALLBACKS: ICampaignVariableFallbacks = {
  firstName: '',
  lastName: '',
  company: '',
  title: '',
  senderName: '',
};

const VARIABLE_REGEX = /\{\{\s*(firstName|lastName|company|title|senderName)\s*\}\}/gi;

export function normalizeVariableFallbacks(
  raw?: ICampaignVariableFallbacks | null,
): ICampaignVariableFallbacks {
  const out: ICampaignVariableFallbacks = {};
  for (const { key } of CAMPAIGN_TEMPLATE_VARIABLES) {
    const val = raw?.[key];
    if (val != null && String(val).trim()) out[key] = String(val).trim();
  }
  return out;
}

export function mergeVariableFallbacks(
  userDefaults?: ICampaignVariableFallbacks | null,
  campaignOverrides?: ICampaignVariableFallbacks | null,
): ICampaignVariableFallbacks {
  // System defaults → user defaults → campaign overrides (later layers win).
  return {
    ...normalizeVariableFallbacks(DEFAULT_CAMPAIGN_VARIABLE_FALLBACKS),
    ...normalizeVariableFallbacks(userDefaults),
    ...normalizeVariableFallbacks(campaignOverrides),
  };
}

/**
 * Brace tokens that will be sent as literal text.
 * `{{firstName}}` is recognized (any letter case). `{first_name}`, `{first-name}` and `{{first_name}}` are not.
 */
export function unrecognizedVariableTokens(text: string, extraKeys: string[] = []): string[] {
  if (!text) return [];
  const allowed = new Set([
    ...CAMPAIGN_TEMPLATE_VARIABLES.map(v => v.key.toLowerCase()),
    ...extraKeys.map(k => k.toLowerCase()),
  ]);
  const seen = new Set<string>();
  const out: string[] = [];
  const re = /\{+\s*([A-Za-z][A-Za-z0-9_-]*)\s*\}+/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const raw = match[0];
    const recognized = raw.startsWith('{{') && raw.endsWith('}}') && allowed.has(match[1].toLowerCase());
    const key = raw.toLowerCase();
    if (recognized || seen.has(key)) continue;
    seen.add(key);
    out.push(raw);
  }
  return out;
}

export function unrecognizedVariableMessage(tokens: string[], extraKeys: string[] = []): string {
  const supported = [
    ...CAMPAIGN_TEMPLATE_VARIABLES.map(v => v.token),
    ...extraKeys.map(k => `{{${k}}}`),
  ].join(', ');
  return `${tokens.join(', ')} will be sent as written. Supported variables: ${supported}.`;
}

/** Brace tokens in the sequence that will be sent literally. Matches the field warning. */
export function unrecognizedSequenceVariableTokens(sequence: ISequenceStep[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  const add = (text: string, extraKeys: string[] = []) => {
    for (const token of unrecognizedVariableTokens(text, extraKeys)) {
      const key = token.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(token);
    }
  };
  for (const step of sequence) {
    if (step.type === 'message' || step.type === 'sn_message' || (step.type === 'connection_request' && step.includeNote)) {
      const legacy = step.messageMode === 'ai' && !(step.messageTemplate ?? '').trim();
      const text = (step.messageTemplate ?? '') || (legacy ? (step.messageAiPrompt ?? '') : '');
      add(text);
      if (step.type === 'sn_message') add(step.messageSubject ?? '');
    }
    if (step.type === 'post_comment') {
      const legacy = step.commentMode === 'ai' && !(step.commentTemplate ?? '').trim();
      const text = (step.commentTemplate ?? '') || (legacy ? (step.commentAiPrompt ?? '') : '');
      add(text);
    }
    if (step.type === 'email') {
      const legacyBody = step.emailBodyMode === 'ai' && !(step.emailBodyTemplate ?? '').trim();
      const legacySubject = step.emailSubjectMode === 'ai' && !(step.emailSubjectTemplate ?? '').trim();
      const subject = (step.emailSubjectTemplate ?? '')
        || (legacySubject ? (step.emailSubjectAiPrompt ?? '') : '');
      const body = (step.emailBodyTemplate ?? '')
        || (legacyBody ? (step.emailBodyAiPrompt ?? '') : '');
      const plainBody = body
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(div|p|li|h[1-6])>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/gi, ' ');
      add(`${subject}\n${plainBody}`, ['signature']);
    }
  }
  return out;
}

export function extractVariablesFromText(text: string): CampaignVariableKey[] {
  const found = new Set<CampaignVariableKey>();
  if (!text) return [];
  const re = new RegExp(VARIABLE_REGEX.source, 'gi');
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const key = match[1] as CampaignVariableKey;
    if (CAMPAIGN_TEMPLATE_VARIABLES.some(v => v.key === key)) found.add(key);
  }
  return Array.from(found);
}

export function getMissingVariableFallbacks(
  text: string,
  fallbacks: ICampaignVariableFallbacks,
): CampaignVariableKey[] {
  return extractVariablesFromText(text).filter(k => !(fallbacks[k] ?? '').trim());
}

function resolveValue(leadValue: string, fallback?: string): string {
  const trimmed = (leadValue || '').trim();
  if (trimmed) return trimmed;
  return (fallback ?? '').trim();
}

/** Substitute {{variables}} with lead data, then campaign/user fallbacks when empty. */
export function renderCampaignTemplate(
  tpl: string,
  lead: Pick<ICampaignLead, 'firstName' | 'lastName' | 'name' | 'company' | 'title'>,
  senderName = '',
  fallbacks: ICampaignVariableFallbacks = {},
): string {
  // Honorifics and post-nominals are stripped here, not at ingest: 'Dr. Marcel
  // Walter' must still display as-is in the app, but greet as 'Hallo Marcel'.
  const { firstName: firstFromLead, lastName: lastFromLead } = resolveLeadNameParts(lead);
  const fb = normalizeVariableFallbacks(fallbacks);

  const first = resolveValue(firstFromLead, fb.firstName);
  const last = resolveValue(lastFromLead, fb.lastName);
  const company = resolveValue(lead.company || '', fb.company);
  const title = resolveValue(lead.title || '', fb.title);
  // An address is not a name — same rule as the executor, so the preview matches what is sent.
  const sender = resolveValue(looksLikeEmail(senderName) ? '' : cleanPersonName(senderName), fb.senderName);

  return (tpl || '')
    .replace(/\{\{\s*firstName\s*\}\}/gi, first)
    .replace(/\{\{\s*lastName\s*\}\}/gi, last)
    .replace(/\{\{\s*company\s*\}\}/gi, company)
    .replace(/\{\{\s*title\s*\}\}/gi, title)
    .replace(/\{\{\s*senderName\s*\}\}/gi, sender);
}

/**
 * User-authored template/prompt text only.
 * Do not include finalContent — AI preview may inject {{senderName}} etc. that the user never chose.
 */
function stepTextFields(step: ISequenceStep): string[] {
  const fields: string[] = [];
  if (step.type === 'message' || step.type === 'sn_message') {
    if (step.messageMode === 'template' && step.messageTemplate?.trim()) fields.push(step.messageTemplate);
    if (step.messageMode === 'ai' && step.messageAiPrompt?.trim()) fields.push(step.messageAiPrompt);
    if (step.type === 'sn_message' && step.messageSubject?.trim()) fields.push(step.messageSubject);
  }
  if (step.type === 'post_comment') {
    if (step.commentMode === 'template' && step.commentTemplate?.trim()) fields.push(step.commentTemplate);
    if (step.commentMode === 'ai' && step.commentAiPrompt?.trim()) fields.push(step.commentAiPrompt);
  }
  if (step.type === 'connection_request' && step.includeNote) {
    if (step.messageMode === 'template' && step.messageTemplate?.trim()) fields.push(step.messageTemplate);
    if (step.messageMode === 'ai' && step.messageAiPrompt?.trim()) fields.push(step.messageAiPrompt);
  }
  return fields;
}

export function collectSequenceVariables(sequence: ISequenceStep[]): CampaignVariableKey[] {
  const found = new Set<CampaignVariableKey>();
  for (const step of sequence) {
    for (const text of stepTextFields(step)) {
      extractVariablesFromText(text).forEach(k => found.add(k));
    }
  }
  return Array.from(found);
}

export function getSequenceMissingFallbacks(
  sequence: ISequenceStep[],
  fallbacks: ICampaignVariableFallbacks,
): CampaignVariableKey[] {
  const missing = new Set<CampaignVariableKey>();
  for (const step of sequence) {
    for (const text of stepTextFields(step)) {
      getMissingVariableFallbacks(text, fallbacks).forEach(k => missing.add(k));
    }
  }
  return Array.from(missing);
}

export function variableLabel(key: CampaignVariableKey): string {
  return CAMPAIGN_TEMPLATE_VARIABLES.find(v => v.key === key)?.label ?? key;
}

export function variableToken(key: CampaignVariableKey): string {
  return CAMPAIGN_TEMPLATE_VARIABLES.find(v => v.key === key)?.token ?? `{{${key}}}`;
}
